import React, { useState } from 'react'
import { Checkbox, Col, DatePicker, Divider, Form, InputNumber, Modal, Radio, Tooltip } from 'antd'
import { CloseCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { IUpdateWorkweekFormProps } from './types'
import IntlMessages from '../../util/IntlMessages'
import { sortWorkWeekFromDay } from '../../util/WorkWeek'
import { 
  getAverageWorkingHours,
  getWeekStartDateFromDate,
  HOURS_MAX, HOURS_MIN, HOURS_STEP, isUserWorkHoursFeatureAllowed, isUserWorkHoursScheduleFeatureAllowed, userHasRotatingSchedule, userHasWorkingHoursSchedule
} from '@vacationtracker/shared/functions/work-week'
import { WorkingHoursMatrix, WorkWeekTablePicker } from '../work-week/tablepicker'
import dayjs, { Dayjs } from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '@vacationtracker/shared/data/app-parameters'
import { IWorkingSchedule, WorkingHours } from '@vacationtracker/shared/types/work-week'
import { DayOfWeek } from '@vacationtracker/shared/types/work-week'

interface IFormValues {
  workWeek: number[]
  hours: number
  workHours: WorkingHoursMatrix[]
  startDate: Dayjs
}

const UpdateWorkweekForm = ({
  userWorkHours,
  companyPlan,
  hourlyLeaveAccounting,
  featureFlags,
  firstDayOfTheWeek = 1,
  handleSave,
  handleCancel,
  visible,
}: IUpdateWorkweekFormProps) => {
  const [form] = Form.useForm()

  dayjs.extend(updateLocale)
  dayjs.updateLocale('en', { weekStart: firstDayOfTheWeek })

  const isSettingWorkHoursAllowed = isUserWorkHoursFeatureAllowed(featureFlags, companyPlan)
  const isSettingWorkHoursScheduleAllowed = isUserWorkHoursScheduleFeatureAllowed(featureFlags, hourlyLeaveAccounting, companyPlan)

  const [confirmLoading, setConfirmLoading] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [userHasSchedule, setUserHasSchedule] = useState(isSettingWorkHoursScheduleAllowed && userHasWorkingHoursSchedule(userWorkHours))
  const [startDate, setStartDate] = useState(
    userHasRotatingSchedule(userWorkHours)
      ? dayjs((userWorkHours as IWorkingSchedule).startDate)
      : dayjs(getWeekStartDateFromDate(firstDayOfTheWeek, new Date()) as Date)
  )

  const defaultValue = {
    hours: userHasRotatingSchedule(userWorkHours)
      ? DEFAULT_WORKING_HOURS_IN_DAY
      : getAverageWorkingHours(userWorkHours) || DEFAULT_WORKING_HOURS_IN_DAY,
    workWeek: userHasRotatingSchedule(userWorkHours)
      ? (userWorkHours as IWorkingSchedule).workHours[0].map(([day]) => day)
      : (userWorkHours as WorkingHours).map(([day]) => day) || [1,2,3,4,5],
    startDate: userHasRotatingSchedule(userWorkHours)
      ? dayjs((userWorkHours as IWorkingSchedule).startDate)
      : dayjs(getWeekStartDateFromDate(firstDayOfTheWeek, new Date()) as Date),
    workHours: userHasRotatingSchedule(userWorkHours)
      ? (userWorkHours as IWorkingSchedule).workHours.flatMap((week, weekIndex) =>
        week.map(([day, hours]): WorkingHoursMatrix => [day, hours, weekIndex])
      )
      : Array.isArray(userWorkHours) ? userWorkHours.map(item => ([item[0], item[1], 0] as WorkingHoursMatrix)) : [],
  }

  const onFinish = () => {
    const values = form.getFieldsValue() as IFormValues
    let data: WorkingHours | IWorkingSchedule
    if (values?.workHours?.length > 0) {
      // Reshaping matrix to array of weeks with array of tuples [day, hours]
      const workHours = values.workHours
        .sort((a, b) => a[2] - b[2])  // Sort by weekIndex (index 2 - last in matrix)
        .reduce((acc: WorkingHours[], [day, hours, weekIndex]) => {
          if (!acc[weekIndex]) acc[weekIndex] = []
          if (hours === 0) return acc  // Skip zero hours
          acc[weekIndex].push([day as DayOfWeek, hours])
          return acc
        }, [])
        .filter(week => week.length > 0)  // Remove empty weeks

      data = {
        workHours,
        startDate: values.startDate.startOf('day').toISOString(),
      } as IWorkingSchedule
    } else {
      data = values.workWeek.map((day) => [day, values.hours]) as WorkingHours
    }
    setConfirmLoading(true)
    handleSave(data)
    setConfirmLoading(false)
  }

  return (
    <Modal
      title={<IntlMessages id="components.updateWorkweekForm.title" />}
      open={visible}
      closeIcon={<CloseCircleOutlined />}
      confirmLoading={confirmLoading}
      okText={<IntlMessages id="app.update" />}
      onOk={onFinish}
      cancelText={<IntlMessages id="app.cancel" />}
      onCancel={handleCancel}
      test-id="update-work-week-modal"
      width="fit-content"
      centered
      styles={{
        body: {
          minWidth: window.innerWidth > 768 ? '650px' : '100%',
        },
      }}
    >
      <Form
        key="updateWorkweekForm"
        form={form}
        name="updateWorkweekForm"
        onFinish={onFinish}
        initialValues={defaultValue}
        data-testid="update-workweek-form"
      >
        <Col>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>
              <IntlMessages id="components.locationForm.settings.workWeekScheduleSet" />
              <Tooltip className="info-tooltip" title={
                <>
                  <IntlMessages id="components.locationForm.settings.workWeekScheduleSetInfo" />
                  {!hourlyLeaveAccounting && <>&nbsp;<IntlMessages id="components.locationForm.settings.workWeekScheduleSetTooltip" /></>}
                  {['Core', 'Freemium'].includes(companyPlan || '') && <>&nbsp;<IntlMessages id="app.feature.access" /></>}
                </>
              }>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <Radio.Group
              disabled={!isSettingWorkHoursScheduleAllowed}
              buttonStyle="solid"
              options={[
                { label: <IntlMessages id="app.yes" />, value: true },
                { label: <IntlMessages id="app.no" />, value: false },
              ]}
              defaultValue={userHasSchedule}
              optionType="button"
              onChange={() => {setUserHasSchedule(!userHasSchedule)}}
            />
          </div>
          
          {!userHasSchedule &&  <>
            <Divider />
            <Form.Item
              name="workWeek"
              className='wrap-label'
              label={<IntlMessages id="app.workweek" />}
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
            >
              <Checkbox.Group>
                {sortWorkWeekFromDay(firstDayOfTheWeek).map((option) => (
                  <Checkbox key={option.value} value={option.value} data-testid={`checkbox-${option.value}`}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
            <Form.Item
              name="hours"
              className='wrap-label'
              label={
                <span>
                  <IntlMessages id="app.workday" />
                  {['Core', 'Freemium'].includes(companyPlan || '') &&
                    <Tooltip className="info-tooltip" title={<IntlMessages id="app.feature.access" />}><InfoCircleOutlined />
                    </Tooltip>
                  }
                </span>
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <InputNumber
                  disabled={!isSettingWorkHoursAllowed}
                  step={HOURS_STEP}
                  min={HOURS_MIN}
                  max={HOURS_MAX}
                  style={{ width: 70 }}
                  defaultValue={defaultValue.hours}
                  precision={1}
                  parser={(value) => {
                    const parsed = parseFloat(value || '0')
                    if (isNaN(parsed)) return HOURS_MIN
                    const rounded = Math.round(parsed * 2) / 2
                    if (rounded < HOURS_MIN) return HOURS_MIN
                    if (rounded > HOURS_MAX) return HOURS_MAX
                    return rounded
                  }}
                  onChange={(value) => {form.setFieldsValue({ hours: value })}}
                />
                <IntlMessages id="app.hours" />
              </div>
            </Form.Item>
          </>}
          
          {userHasSchedule && isSettingWorkHoursScheduleAllowed && (
            <>
              <Form.Item 
                name="startDate" 
                label={
                  <span>
                    <IntlMessages id="app.weekStart" />
                    <Tooltip className="info-tooltip" title={<IntlMessages id="user.workWeek.weekStartTooltip" />}><InfoCircleOutlined /></Tooltip>
                  </span>
                }
              >
                <DatePicker 
                  picker="week" 
                  disabledDate={(current) => {
                    return current && (current.year() !== dayjs().year())
                  }}
                  onChange={(date) => {
                    if (date) {
                      setStartDate(dayjs(getWeekStartDateFromDate(firstDayOfTheWeek, date.toDate())))
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="workHours"
                initialValue={defaultValue.workHours}
              >
                <WorkWeekTablePicker
                  key={startDate.toISOString()}
                  firstDayOfWeek={firstDayOfTheWeek as 0 | 1}
                  workingHours={defaultValue.workHours}
                  startDate={startDate.toDate()}
                  onChange={(hours) => {
                    form.setFieldsValue({ workHours: hours })
                    setShowWarning(true)
                  }}
                />
              </Form.Item>
            </>
          )}
          <Divider />
          {showWarning && <div><WarningOutlined /> <IntlMessages id="components.locationForm.warningUserHoursChange" /></div>}
        </Col>
      </Form>
    </Modal>
  )
}
export default UpdateWorkweekForm
